import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import type { FieldCatalogResponse } from 'features/charts-v2/types/yogiql'
import { warnYogiQLFallback } from 'features/charts-v2/utils'
import { fetchFilterOptionsV2 } from 'features/filters/service/fetch-filter-options-v2'
import type { FilterService } from 'features/filters/service/service'
import type { FilterOption } from 'features/filters/types'
import {
  filterControlQuery,
  toFilterOption,
} from 'features/filters/utils/yogiql-filter-control'

/**
 * The picker's options for ``fields`` under ``enhanced_dialect``: each
 * control's counts come from one YogiQL query to
 * ``/client/yogiql-filter-options``, returned in the legacy option shape. A
 * control with no YogiQL encoding (a theme or dataset filter whose uuid the
 * options cannot name, or a field YogiQL has no control for) is asked of
 * the legacy route instead, after one devtools warning, so the picker always
 * gets its counts.
 */
export const fetchYogiQLFilterOptions = async (
  projectId: string,
  panel: ChartPanel,
  filterService: FilterService,
  context: {
    fields: string[]
    /** Leave each control's own filters out (the picker); see `fetchFilterOptionsV2`. */
    faceted: boolean
    catalog: FieldCatalogResponse
    /** The hub's cached page-load options: each control's label, bounds and
     *  full list of values, and the theme and dataset names a panel's uuids
     *  resolve to. */
    hubOptions: FilterOption[] | undefined
  },
  signal?: AbortSignal,
): Promise<FilterOption[]> => {
  const options = await Promise.all(
    context.fields.map(async (field) => {
      const query = filterControlQuery(
        field,
        panel,
        context.catalog,
        context.hubOptions,
        context.faceted,
      )
      if (typeof query === 'string') {
        warnYogiQLFallback(
          `filter control '${field}' uses the legacy options: ${query}`,
        )
        const legacy = await fetchFilterOptionsV2(
          projectId,
          panel,
          filterService,
          { fields: [field], faceted: context.faceted },
          signal,
        )
        return legacy.filter((option) => option.field === field)
      }
      // A route error fails the control on purpose: under the flag a YogiQL bug
      // should show, not hide behind v2's answer.
      const result = await filterService.getYogiQLFilterOptions(
        projectId,
        query.request,
        { signal },
      )
      const base = context.hubOptions?.find((o) => o.field === field)
      return [toFilterOption(field, query.shape, result, base)]
    }),
  )
  return options.flat()
}

/**
 * The panel's filter options for ``fields``: from YogiQL queries when the
 * hub's ``yogiql`` context is given (``enhanced_dialect`` on, catalog loaded),
 * else from the legacy per-panel route.
 */
export const fetchPanelFilterOptions = (
  projectId: string,
  panel: ChartPanel | null,
  filterService: FilterService,
  options: {
    fields: string[] | undefined
    faceted: boolean
    yogiql: {
      catalog: FieldCatalogResponse
      hubOptions: FilterOption[] | undefined
    } | null
  },
  signal?: AbortSignal,
): Promise<FilterOption[]> =>
  options.yogiql && panel
    ? fetchYogiQLFilterOptions(
        projectId,
        panel,
        filterService,
        {
          fields: options.fields ?? [],
          faceted: options.faceted,
          ...options.yogiql,
        },
        signal,
      )
    : fetchFilterOptionsV2(
        projectId,
        panel,
        filterService,
        { fields: options.fields, faceted: options.faceted },
        signal,
      )
